import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  Search,
  Coins,
  CalendarClock,
  X,
  Check,
  ScanLine,
  ChevronRight,
  ArrowLeft,
  User,
  Stethoscope,
  Camera,
  Heart,
  Syringe,
  Activity,
  GraduationCap,
  Dumbbell,
  Scale,
  Brain,
  Moon,
  Apple,
  Loader2
} from 'lucide-react'
import { eventsApi, pointsApi, medicalApi } from '../../lib/api'

const eventIcons: Record<string, any> = {
  general_health: CalendarClock,
  blood_pressure: Heart,
  blood_sugar: Syringe,
  vaccination: Syringe,
  health_exam: Activity,
  health_education: GraduationCap,
  smoking_cessation: Heart,
  exercise: Dumbbell,
  weight_management: Scale,
  mental_health: Brain,
  sleep: Moon,
  nutrition: Apple,
}

const UserPointEntry = () => {
  usePageTitle()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<any | null>(null)
  const [selectedEventId, setSelectedEventId] = useState('')
  const [currentStep, setCurrentStep] = useState(1)
  const [successModal, setSuccessModal] = useState(false)
  const [addedPoints, setAddedPoints] = useState(0)
  const [inputMode, setInputMode] = useState<'manual' | 'scan'>('manual')
  const [eventList, setEventList] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [scanLoading, setScanLoading] = useState(false)
  const [remark, setRemark] = useState('')
  const [entryTime, setEntryTime] = useState(() => {
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  })
  const [useHistoryTime, setUseHistoryTime] = useState(false)
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [errorModal, setErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // 搜索用户
  const searchUsers = (keyword: string) => {
    if (!keyword || keyword.length < 1) {
      setSearchResults([])
      return
    }
    setSearchLoading(true)
    medicalApi.searchUsers(keyword, 20)
      .then((res: any) => {
        setSearchResults(res.users || [])
      })
      .catch(() => {
        setSearchResults([])
      })
      .finally(() => setSearchLoading(false))
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // 每次页面显示时从后端获取最新医护信息
  useEffect(() => {
    const fetchMedicalInfo = async () => {
      try {
        const info = await medicalApi.me() as any
        if (info && info.hospital_id) {
          const cached = localStorage.getItem('hospitalCache')
          const cache = cached ? JSON.parse(cached) : {}
          cache[info.hospital_id] = { id: info.hospital_id, name: info.hospital_name, cachedAt: Date.now() }
          localStorage.setItem('hospitalCache', JSON.stringify(cache))

          const medicalUser = JSON.parse(localStorage.getItem('medicalUser') || '{}')
          medicalUser.hospital_id = info.hospital_id
          medicalUser.hospital = info.hospital_name
          medicalUser.hospital_name = info.hospital_name
          localStorage.setItem('medicalUser', JSON.stringify(medicalUser))
        }
      } catch (e) {
        // 忽略错误，使用本地缓存
      }
    }
    fetchMedicalInfo()
  }, [])

  // 搜索时实时查询
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search && search.length >= 1) {
        searchUsers(search)
      } else {
        setSearchResults([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // 检测小程序扫码结果（通过 URL 参数 _scanUid 传递）
  useEffect(() => {
    const scanUid = searchParams.get('_scanUid')
    const scanTs = searchParams.get('_scanTs')

    if (scanUid) {
      // 时间戳验证：5分钟内有效（开发环境 localhost 放宽至24小时方便调试）
      const tsNum = scanTs ? parseInt(scanTs) : 0
      const age = Date.now() - tsNum
      const maxAge = window.location.hostname === 'localhost' ? 24 * 60 * 60 * 1000 : 5 * 60 * 1000
      const isValid = !scanTs || age < maxAge

      if (isValid) {
        setScanLoading(true)
        medicalApi.getUserById(scanUid)
          .then((user: any) => {
            setSelectedUser(user)
            setCurrentStep(2)
            setInputMode('manual')
          })
          .catch((err: any) => {
            console.error('扫码查用户失败:', err)
            const status = err?.statusCode || 0
            if (status === 404) {
              setErrorMessage('该二维码对应的用户不存在，请确认用户是否已注册')
            } else if (status === 403) {
              setErrorMessage('该用户不属于您所在的机构，无法为其录入积分')
            } else {
              setErrorMessage(err?.message || '识别用户失败，请重新扫码')
            }
            setErrorModal(true)
          })
          .finally(() => setScanLoading(false))

        // 清理 URL 参数
        const url = new URL(window.location.href)
        url.searchParams.delete('_scanUid')
        url.searchParams.delete('_scanTs')
        window.history.replaceState({}, '', url.toString())
      } else {
        // 时间戳过期，清理URL参数
        const url = new URL(window.location.href)
        url.searchParams.delete('_scanUid')
        url.searchParams.delete('_scanTs')
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [searchParams])

  // 初始加载事件列表
  useEffect(() => {
    setLoading(true)
    eventsApi.list({ is_active: true })
      .then((res: any) => {
        setEventList(res.event_types || [])
      })
      .catch(() => {
        setEventList([])
      })
      .finally(() => setLoading(false))
  }, [])

  // 从医护人员信息中获取机构，而非用户归属机构
  const medicalUser = JSON.parse(localStorage.getItem('medicalUser') || '{}')
  const hospital = medicalUser.hospital || medicalUser.hospital_name || '未设置机构'

  const displayUsers = search ? searchResults : []

  const handleSelectUser = (user: any) => {
    setSelectedUser(user)
    setSearch('')
    setSearchResults([])
    setCurrentStep(2)
  }

  const handleConfirm = () => {
    if (!selectedUser || !selectedEvent) return
    pointsApi.create({
      user_id: selectedUser.id,
      event_type_id: selectedEvent.id,
      hospital_id: medicalUser.hospital_id || '',
      remark: remark.trim() || undefined,
      entry_time: useHistoryTime ? entryTime : undefined,
    }).then(() => {
      setAddedPoints(selectedEvent.points)
      setSuccessModal(true)
      // 保留 selectedUser：只清事件选择，停在步骤2，方便为同一会员连续录入多项
      setSelectedEventId('')
      setCurrentStep(2)
      setRemark('')
      const d = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      setEntryTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
      setUseHistoryTime(false)
    }).catch((err: any) => {
      const msg = err?.message || '录入失败，请稍后重试'
      setErrorMessage(msg)
      setErrorModal(true)
    })
  }

  // 切换用户：清空所有用户相关状态，回到步骤1
  const handleChangeUser = () => {
    setSelectedUser(null)
    setSelectedEventId('')
    setCurrentStep(1)
    setRemark('')
    setInputMode('manual')
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    setEntryTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
    setUseHistoryTime(false)
  }

  // 扫码录入：通过 wx.miniProgram.navigateTo 跳转到原生扫码页面（无需签名）
  const handleScan = () => {
    const isMiniProgram = /miniProgram/i.test(navigator.userAgent) || window.__wxjs_environment === 'miniprogram'

    if (isMiniProgram && window.wx) {
      // 方式1：直接跳转（推荐）
      try {
        window.wx.miniProgram.navigateTo({ url: '/pages/scan-entry/index' })
        return
      } catch (e) {
        console.warn('navigateTo 失败，尝试 postMessage', e)
      }
      // 方式2：通过 postMessage fallback
      window.wx.miniProgram.postMessage({ type: 'navigate', url: '/pages/scan-entry/index' })
    } else {
      // H5 开发环境：手动输入用户ID模拟扫码
      const uid = prompt('请输入用户ID（模拟扫码）：')
      if (uid && /^[a-f0-9-]{36}$/i.test(uid.trim())) {
        setScanLoading(true)
        medicalApi.getUserById(uid.trim())
          .then((user: any) => {
            setSelectedUser(user)
            setCurrentStep(2)
            setInputMode('manual')
          })
          .catch((err: any) => {
            const status = err?.statusCode || 0
            if (status === 404) {
              setErrorMessage('该用户不存在，请确认用户是否已注册')
            } else if (status === 403) {
              setErrorMessage('该用户不属于您所在的机构，无法为其录入积分')
            } else {
              setErrorMessage(err?.message || '识别用户失败，请重试')
            }
            setErrorModal(true)
          })
          .finally(() => setScanLoading(false))
      } else if (uid) {
        alert('用户ID格式不正确')
      }
    }
  }

  const availableEvents = eventList.filter(e =>
    e.enabled && (!e.hospital_ids || e.hospital_ids.includes(medicalUser.hospital_id))
  )

  const selectedEvent = eventList.find(e => e.id === selectedEventId)

  return (
    <div className="bg-gray-50 min-h-screen pb-6">
      {/* 顶部导航 */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-4 flex items-center space-x-4 sticky top-0 z-10">
        <h1 className="text-lg font-bold text-white">积分录入</h1>
        <div className="ml-auto text-green-100 text-xs">{hospital}</div>
      </div>

      {/* 步骤指示 */}
      <div className="px-4 py-3 bg-white border-b">
        <div className="flex items-center space-x-2 text-sm">
          <div className={`flex items-center space-x-1 ${(currentStep >= 1 || selectedUser) ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${selectedUser ? 'bg-green-600 text-white' : currentStep >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {selectedUser ? <Check className="w-3.5 h-3.5" /> : '1'}
            </div>
            <span>选择用户</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <div className={`flex items-center space-x-1 ${currentStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
            <span>选择事件</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <div className={`flex items-center space-x-1 ${currentStep >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep >= 3 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
            <span>确认录入</span>
          </div>
        </div>
      </div>

      {/* 模式切换：手动 vs 扫码 */}
      {currentStep === 1 && (
        <div className="px-4 pt-4">
          {selectedUser ? (
            /* B1 形态：已选用户卡 + 重新扫码 / 换个用户 双按钮 */
            <div className="bg-white rounded-xl shadow-md p-5 border-2 border-green-200">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-green-600 font-medium">当前服务用户</p>
                <button onClick={handleChangeUser} className="text-xs text-gray-400 hover:text-gray-600 flex items-center space-x-1">
                  <X className="w-3 h-3" />
                  <span>取消选择</span>
                </button>
              </div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-14 h-14 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="text-white font-bold text-xl">{(selectedUser.name || '').charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 text-lg">{selectedUser.name || ''}</p>
                  <p className="text-xs text-gray-400">{selectedUser.phone || ''}</p>
                </div>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">已选中</span>
              </div>
              <button
                onClick={() => setCurrentStep(2)}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
              >
                继续为 {selectedUser.name || '该用户'} 录入积分
              </button>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  onClick={() => { setInputMode('scan'); handleScan() }}
                  disabled={scanLoading}
                  className="py-2.5 border-2 border-gray-200 rounded-xl text-sm text-gray-600 hover:border-green-400 hover:text-green-700 transition-colors flex items-center justify-center space-x-1.5 disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  <span>重新扫码</span>
                </button>
                <button
                  onClick={handleChangeUser}
                  className="py-2.5 border-2 border-gray-200 rounded-xl text-sm text-gray-600 hover:border-green-400 hover:text-green-700 transition-colors flex items-center justify-center space-x-1.5"
                >
                  <Search className="w-4 h-4" />
                  <span>换个用户</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex bg-white rounded-xl shadow-md overflow-hidden mb-4">
                <button
                  onClick={() => setInputMode('manual')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${inputMode === 'manual' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  手动搜索用户
                </button>
                <button
                  onClick={() => setInputMode('scan')}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${inputMode === 'scan' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  扫码录入
                </button>
              </div>

              {inputMode === 'scan' ? (
                /* 扫码模式 - 点击按钮唤起扫码 */
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="p-8 flex flex-col items-center">
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-4">
                      <ScanLine className="w-10 h-10 text-green-600" />
                    </div>
                    <p className="text-gray-700 font-medium mb-1">扫描用户身份码</p>
                    <p className="text-gray-400 text-sm mb-6 text-center px-4">
                      让用户打开"身份码"页面，点击下方按钮扫描二维码
                    </p>
                    {scanLoading ? (
                      <div className="flex items-center space-x-2 text-green-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>正在识别用户...</span>
                      </div>
                    ) : (
                      <button
                        onClick={handleScan}
                        className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Camera className="w-5 h-5" />
                        <span>开始扫码</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* 手动搜索模式 */
                <div className="bg-white rounded-xl shadow-md p-4">
                  <div className="relative mb-4">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="输入姓名、手机号或身份证搜索"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 text-base border-2 border-gray-100 rounded-xl focus:outline-none focus:border-green-400 bg-gray-50"
                    />
                    {searchLoading && (
                      <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                    )}
                  </div>
                  {search && (
                    <div className="border-2 border-gray-100 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
                      {displayUsers.length === 0 ? (
                        <div className="p-4 text-center text-gray-400">未找到匹配的用户</div>
                      ) : displayUsers.slice(0, 6).map(u => (
                        <button
                          key={u.id}
                          onClick={() => handleSelectUser(u)}
                          className="w-full flex items-center space-x-3 p-4 hover:bg-green-50 transition-colors border-b border-gray-50 last:border-b-0"
                        >
                          <div className="w-11 h-11 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm">{(u.name || '').charAt(0)}</span>
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-semibold text-gray-800">{u.name || ''}</p>
                            <p className="text-xs text-gray-400">{u.phone || ''} · {(u.id_card || '').slice(-6)}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 已选用户卡片 - 步骤2/3顶部常驻 */}
      {selectedUser && currentStep >= 2 && (
        <div className="px-4 pt-4">
          <div className="bg-white rounded-xl shadow-md p-3 flex items-center space-x-3 border border-green-100">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">{(selectedUser.name || '').charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400">正在为</p>
              <p className="font-semibold text-gray-800 text-sm truncate">{selectedUser.name || ''} · {selectedUser.phone || ''}</p>
            </div>
            <button
              onClick={handleChangeUser}
              className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors flex items-center space-x-1"
            >
              <X className="w-3 h-3" />
              <span>切换用户</span>
            </button>
          </div>
        </div>
      )}

      {/* 步骤2：选择事件 */}
      {currentStep === 2 && selectedUser && (
        <div className="px-4 pt-4">
          <p className="text-sm font-medium text-gray-500 mb-2">请选择事件类型</p>
          <div className="grid grid-cols-2 gap-3">
            {availableEvents.map(e => {
              const Icon = eventIcons[e.id] || CalendarClock
              const isSelected = selectedEventId === e.id
              return (
                <button
                  key={e.id}
                  onClick={() => setSelectedEventId(e.id)}
                  className={`bg-white rounded-xl p-4 border-2 text-left transition-all ${isSelected ? 'border-green-500 shadow-md' : 'border-gray-100 hover:border-green-300'}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${isSelected ? 'bg-green-600' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <p className={`text-sm font-medium ${isSelected ? 'text-green-700' : 'text-gray-700'}`}>{e.name || ''}</p>
                  <p className={`text-sm font-black mt-1 ${isSelected ? 'text-green-600' : 'text-gray-400'}`}>+{e.points || 0}分</p>
                </button>
              )
            })}
          </div>

          <div className="flex space-x-3 mt-4">
            <button onClick={() => setCurrentStep(1)} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">上一步</button>
            <button
              onClick={() => setCurrentStep(3)}
              disabled={!selectedEventId}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              下一步
            </button>
          </div>
        </div>
      )}

      {/* 步骤3：确认录入 */}
      {currentStep === 3 && selectedUser && selectedEvent && (
        <div className="px-4 pt-4">
          <div className="bg-white rounded-xl shadow-md p-5">
            <h3 className="font-bold text-gray-800 mb-4">确认积分信息</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">用户姓名</span>
                <span className="font-medium text-gray-800">{selectedUser.name || ''}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">手机号</span>
                <span className="font-medium text-gray-800">{selectedUser.phone || ''}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">所属机构</span>
                <span className="font-medium text-gray-800">{hospital}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">事件类型</span>
                <span className="font-medium text-gray-800">{selectedEvent?.name || ''}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">积分周期</span>
                <span className="text-xs text-gray-400">{({day:'每天',week:'每周',month:'每月',quarter:'每季度',year:'每年'} as Record<string,string>)[selectedEvent?.cycle] || '一次性'}{selectedEvent?.cycle_limit ?? 1}次</span>
              </div>
              <div className="flex justify-between py-3 mt-2">
                <span className="text-gray-500">获得积分</span>
                <span className="text-2xl font-black text-green-600">+{selectedEvent?.points || 0}</span>
              </div>
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-500">录入时间</label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={useHistoryTime} onChange={e => setUseHistoryTime(e.target.checked)} className="w-4 h-4 accent-green-600" />
                    指定历史时间
                  </label>
                </div>
                {useHistoryTime ? (
                  <>
                    <input type="datetime-local" value={entryTime} onChange={e => setEntryTime(e.target.value)} className="w-full px-3 py-2 border border-amber-300 rounded-xl text-sm focus:outline-none focus:border-amber-400 bg-amber-50" />
                    <p className="text-xs text-amber-600 mt-1">周期限制将按此历史时间计算</p>
                  </>
                ) : (
                  <p className="text-gray-700 text-sm">当前时间（提交时自动记录）</p>
                )}
              </div>
              <div className="border-t border-gray-100 pt-3 mt-3">
                <label className="text-sm text-gray-500 mb-1 block">备注（可选）</label>
                <textarea
                  value={remark}
                  onChange={e => setRemark(e.target.value.slice(0, 500))}
                  placeholder="可填写补充说明，如特殊情况说明等"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none focus:border-green-400"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-4">
            <button onClick={() => setCurrentStep(2)} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">上一步</button>
            <button onClick={handleConfirm} className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700">确认录入</button>
          </div>
        </div>
      )}

      {/* 成功弹窗 */}
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-3xl font-black text-green-600 mb-1">+{addedPoints}</p>
              <p className="text-gray-500 text-sm">积分添加成功！</p>
            </div>
            <div className="p-4 border-t flex space-x-3">
              <button onClick={() => { setSuccessModal(false); setCurrentStep(2) }} className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors">
                继续为 {selectedUser?.name || '该用户'} 录入
              </button>
              <button onClick={() => { setSuccessModal(false); handleChangeUser() }} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
                换用户
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 错误弹窗 */}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-lg font-bold text-gray-800 mb-2">录入失败</p>
              <p className="text-sm text-gray-500">{errorMessage}</p>
            </div>
            <div className="p-4 border-t">
              <button onClick={() => setErrorModal(false)} className="w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700">知道了</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserPointEntry